import type { Key } from "react-aria-components";

import { RequestTypeEnum } from "@nlc/shared-types";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { Label } from "react-aria-components";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";

interface SubmitWarningModalProps {
  isOpen: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenUnlock: () => void;
}

export function ChangeRequestTypeModal({
  isOpen,
  onOpenChange,
  onOpenUnlock,
}: SubmitWarningModalProps) {
  const { data: request } = useGetRequest();
  const { isTemplate } = useIsTemplate();
  const [selectedType, setSelectedType] =
    React.useState<null | RequestTypeEnum>(null);
  const queryClient = useQueryClient();
  const { updateField } = useFieldUpdate();

  const handleChangeType = async () => {
    const { success } = await updateField("requestType", selectedType);

    if (success) {
      onOpenChange(false);
      await queryClient.invalidateQueries({ queryKey: ["request"] });
    }
  };

  return (
    <Modal
      classNames={{ modal: "w-[400px]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog>
        <DialogBody>
          <div className="flex flex-col gap-xs">
            <span className="text-lg font-semibold text-text-primary">
              Change Request Type
            </span>
            <DialogCloseButton />
          </div>
          <TypeSelector
            currentType={request?.requestType as RequestTypeEnum}
            onTypeChange={setSelectedType}
          />
        </DialogBody>
        <DialogFooter className="flex flex-grow-1 mt-lg">
          <DialogCloseButton>Cancel</DialogCloseButton>
          <Button
            className="flex-1"
            color="gray"
            onPress={() => onOpenChange(false)}
            size="lg"
            variant="secondary"
          >
            Never mind
          </Button>
          <Button
            className="flex-1"
            onPress={
              isTemplate
                ? () => {
                    onOpenUnlock();
                    onOpenChange(false);
                  }
                : handleChangeType
            }
            size="lg"
            variant="primary"
          >
            Confirm
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

interface TypeSelectorProps {
  currentType: RequestTypeEnum;
  onTypeChange: (type: null | RequestTypeEnum) => void;
}

function TypeSelector({ currentType, onTypeChange }: TypeSelectorProps) {
  const [selectedType, setSelectedType] = React.useState<Key>(currentType);

  const handleTypeChange = (key: Key | null) => {
    if (key === null) return;
    setSelectedType(key);
    onTypeChange(key as RequestTypeEnum);
  };

  return (
    <Select
      onSelectionChange={handleTypeChange}
      placeholder="Select request type..."
      selectedKey={selectedType}
    >
      <Label>
        You are about to change the type of this request. Please select the new
        request type:
      </Label>
      <SelectButton className="mt-sm" />
      <SelectPopover>
        <SelectListBox>
          <SelectListItem id={RequestTypeEnum.GRAPHIC}>
            Graphic Design
          </SelectListItem>
          <SelectListItem id={RequestTypeEnum.VIDEO}>Video</SelectListItem>
          <SelectListItem id={RequestTypeEnum.WEBSITE_DESIGN}>
            Website Design
          </SelectListItem>
          <SelectListItem id={RequestTypeEnum.WEBSITE_IMPLEMENTATION}>
            Website Implementation
          </SelectListItem>
        </SelectListBox>
      </SelectPopover>
    </Select>
  );
}
